import type { ReactNode } from "react";
import type { NavIconName } from "./navConfig";

const stroke = {
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

function IconFrame({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      width={24}
      height={24}
      className="h-6 w-6 shrink-0"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export function NavIcon({ name }: { name: NavIconName }) {
  switch (name) {
    case "access":
      return (
        <IconFrame>
          <circle cx="7.5" cy="15.5" r="3.25" {...stroke} />
          <path d="M10.4 13.7 20 4.2" {...stroke} />
          <path d="M16.8 4.2h3.2v3.2" {...stroke} />
          <path d="M7.5 15.5h.01" {...stroke} />
        </IconFrame>
      );
    case "operations":
      return (
        <IconFrame>
          <rect x="3.5" y="3.5" width="7.5" height="9" rx="1.5" {...stroke} />
          <rect x="13" y="3.5" width="7.5" height="5.5" rx="1.5" {...stroke} />
          <rect x="13" y="11.5" width="7.5" height="9" rx="1.5" {...stroke} />
          <rect x="3.5" y="15" width="7.5" height="5.5" rx="1.5" {...stroke} />
        </IconFrame>
      );
    case "communities":
      return (
        <IconFrame>
          <path d="M3.5 21V9.2L10 4.5l6.5 4.7V21" {...stroke} />
          <path d="M16.5 11.5V21H21V12l-4.5-2" {...stroke} />
          <path d="M9 21v-5.5h5V21" {...stroke} />
        </IconFrame>
      );
    case "communications":
      return (
        <IconFrame>
          <path d="M4 9.5v5l6-1.15V10.65L4 9.5Z" {...stroke} />
          <path d="M10 8.4c3.6-1.7 7.4-2.5 11-2.3v11.8c-3.6.2-7.4-.6-11-2.3" {...stroke} />
          <path d="M6.2 14.6 7 20h2.6" {...stroke} />
        </IconFrame>
      );
    case "contact":
      return (
        <IconFrame>
          <path d="M3.5 12h5.2l1.4 2.2h4.8L16.3 12h4.2" {...stroke} />
          <path d="M3.5 12 6.6 6.4A2 2 0 0 1 8.4 5.5h7.2a2 2 0 0 1 1.8.9L20.5 12v6.5a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2V12Z" {...stroke} />
        </IconFrame>
      );
    case "reservations":
      return (
        <IconFrame>
          <rect x="3.5" y="5" width="17" height="15.5" rx="2" {...stroke} />
          <path d="M3.5 10h17M8 3.5v3.5M16 3.5v3.5" {...stroke} />
        </IconFrame>
      );
    case "payments":
      return (
        <IconFrame>
          <path d="M7 3.5h8.2L19.5 7.8V20.5H7A2.5 2.5 0 0 1 4.5 18V6A2.5 2.5 0 0 1 7 3.5Z" {...stroke} />
          <path d="M15.2 3.5V8h4.3M8.2 12h7.5M8.2 16h5" {...stroke} />
        </IconFrame>
      );
    case "users":
      return (
        <IconFrame>
          <circle cx="9" cy="8" r="3" {...stroke} />
          <path d="M3.6 19.5c.5-3.1 2.7-4.9 5.4-4.9s4.9 1.8 5.4 4.9" {...stroke} />
          <circle cx="17" cy="9.2" r="2.2" {...stroke} />
          <path d="M16.4 14.7c1.7.5 3 1.8 3.4 4.3" {...stroke} />
        </IconFrame>
      );
    case "visitors":
      return (
        <IconFrame>
          <rect x="3.5" y="4" width="17" height="16" rx="2" {...stroke} />
          <circle cx="12" cy="10" r="2.35" {...stroke} />
          <path d="M8.3 17c.7-1.7 2-2.6 3.7-2.6s3 .9 3.7 2.6" {...stroke} />
        </IconFrame>
      );
    case "assemblies":
      return (
        <IconFrame>
          <circle cx="12" cy="6.2" r="2.35" {...stroke} />
          <path d="M12 8.6v4.2M7.5 21 12 12.8 16.5 21" {...stroke} />
          <path d="M5.5 16.2h13" {...stroke} />
        </IconFrame>
      );
    case "notifications":
      return (
        <IconFrame>
          <path d="M6.4 16.8h11.2l-1.3-2.3V10.6a4.3 4.3 0 1 0-8.6 0v3.9l-1.3 2.3Z" {...stroke} />
          <path d="M10 18.8a2 2 0 0 0 4 0" {...stroke} />
        </IconFrame>
      );
    case "subscription":
      return (
        <IconFrame>
          <rect x="2.5" y="6" width="19" height="12.5" rx="2" {...stroke} />
          <path d="M2.5 10h19M7 14.5h4.5" {...stroke} />
        </IconFrame>
      );
    case "ledger":
      return (
        <IconFrame>
          <path d="M6 4.5h11.5A1.5 1.5 0 0 1 19 6v14H7.5A1.5 1.5 0 0 1 6 18.5V4.5Z" {...stroke} />
          <path d="M6 4.5A1.5 1.5 0 0 0 4.5 6v12.5A1.5 1.5 0 0 0 6 20" {...stroke} />
          <path d="M9.5 9h6.5M9.5 13h6.5M9.5 17h4" {...stroke} />
        </IconFrame>
      );
    case "more":
      return (
        <IconFrame>
          <circle cx="8" cy="8" r="1.4" {...stroke} />
          <circle cx="16" cy="8" r="1.4" {...stroke} />
          <circle cx="8" cy="16" r="1.4" {...stroke} />
          <circle cx="16" cy="16" r="1.4" {...stroke} />
        </IconFrame>
      );
  }
}
